import React, { Children } from "react";
import {
	Table,
	Paginator as NacrePaginator,
	Checkbox,
	Card
} from "@weezevent/nacre";
import PropTypes from "prop-types";
import ColumnHeaderTitle from "./components/column-header-title";
import DataRow from "./components/data-row";
import useRowSelection from "./hooks/useRowSelection";
import { ROW_SELECTION_PROP_TYPES } from "./proptypes";
import classes from "./smart-table.module.scss";

const Paginator = () => null;

const reactElementHasType = (el, type) => {
	if (!el || typeof el !== "object") {
		return false;
	}

	return el["type"] === type;
};

const State = {
	LOADED: "loaded",
	LOADING: "loading",
	EMPTY: "empty"
};

const SmartTable = ({
	onClickSorting,
	data,
	rowKey,
	columns,
	currentSorting,
	computeRowIsDisabled,
	children,
	onRowClick,
	onLastRowVisible,
	rowSelection,
	customRowClassFn,
	customCellClassFn,
	emptyComponent,
	loading,
	lastRowLoading,
	minWidthDisabled,
	inputFocus
}) => {
	const ref = React.useRef();

	const rowKeyFunction = React.useCallback(
		row => (typeof rowKey === "function" ? rowKey(row) : row[rowKey]),
		[rowKey]
	);

	const allKeys = React.useMemo(() => {
		return data.map(rowKeyFunction);
	}, [data, rowKeyFunction]);

	const selection = useRowSelection(
		allKeys,
		rowSelection?.selected || [],
		rowSelection?.onChangeSelection
	);

	const tableState = React.useMemo(() => {
		if (loading) {
			return State.LOADING;
		}

		if (data.length > 0) {
			return State.LOADED;
		}

		return emptyComponent ? State.EMPTY : State.LOADED;
	}, [data, loading, emptyComponent]);

	React.useEffect(() => {
		if (onLastRowVisible) {
			const onScroll = () => {
				if (
					window.innerHeight + window.scrollY + 100 >
					ref.current.offsetTop + ref.current.scrollHeight
				) {
					onLastRowVisible();
				}
			};

			window.addEventListener("scroll", onScroll);

			return () => {
				window.removeEventListener("scroll", onScroll);
			};
		}
	}, [onLastRowVisible]);

	const renderHeaderRow = () => {
		const rows = columns.map(column => {
			return (
				<ColumnHeaderTitle
					key={column.key}
					onClickSorting={direction =>
						onClickSorting({
							column: column.key,
							direction
						})
					}
					columnData={data.map(row => row[column.key])}
					title={column.label}
					titleAlign={column.align}
					sortable={column.sortable}
					sortingActive={currentSorting?.column === column.key}
					preferredSortingType={column.preferredSortingType}
					customSortingLabels={column.customSortingLabels}
					sortingDropdownDisabled={data.length === 0}
					sortingDirection={currentSorting?.direction}
					width={column.width}
				/>
			);
		});

		return (
			<Table.Tr>
				{rowSelection && (
					<th>
						<Checkbox
							checked={
								selection.isSelectedAll &&
								!loading &&
								data.length > 0
							}
							onChange={selection.toggleSelectionAll}
							disabled={loading || data.length === 0}
						/>
					</th>
				)}
				{rows}
			</Table.Tr>
		);
	};

	const renderRow = rowData => {
		const currentRowKey = rowKeyFunction(rowData);

		const changeSelection = () => {
			if (rowSelection) {
				selection.toggleSelection(currentRowKey);
			}
		};

		return (
			<DataRow
				rowData={rowData}
				columns={columns}
				computeRowIsDisabled={computeRowIsDisabled}
				onRowClick={onRowClick || changeSelection}
				key={currentRowKey}
				selectionEnabled={!!rowSelection}
				selected={selection.checkIsSelected(currentRowKey)}
				onChangeSelection={changeSelection}
				customRowClass={customRowClassFn(rowData)}
				customCellClassFn={customCellClassFn}
			/>
		);
	};

	const renderLoading = () => {
		return (
			<tr
				className={classes["loader-wrapper"]}
				style={{
					height: inputFocus ? ref.current.clientHeight : "100px"
				}}
			>
				<Card.Loader active={true} />
			</tr>
		);
	};

	return (
		<div className={classes.root} ref={ref}>
			{tableState === State.EMPTY ? (
				emptyComponent
			) : (
				<>
					<Table
						className={
							minWidthDisabled
								? undefined
								: classes["smart-table-min-width"]
						}
						useNew
						responsive
					>
						<Table.THead>{renderHeaderRow()}</Table.THead>
						<Table.TBody>
							{tableState === State.LOADED && data.map(renderRow)}
							{tableState === State.LOADING && renderLoading()}
							{lastRowLoading && renderLoading()}
						</Table.TBody>
					</Table>
					<div className={classes["smart-table-pagination"]}>
						{Children.map(children, child => {
							if (reactElementHasType(child, Paginator)) {
								return <NacrePaginator {...child.props} />;
							}

							return null;
						})}
					</div>
				</>
			)}
		</div>
	);
};

SmartTable.propTypes = {
	/** Unique key of table rows */
	rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,

	/** Table data */
	data: PropTypes.arrayOf(PropTypes.object),

	/** Table columns */
	columns: ROW_SELECTION_PROP_TYPES.Columns.isRequired,

	/** Triggered after change sorting in table */
	onClickSorting: PropTypes.func,

	/** Current state of sorting */
	currentSorting: PropTypes.object,

	/** Function that calculates row disable */
	computeRowIsDisabled: PropTypes.func,

	/** children prop (used for pagination) */
	children: PropTypes.node,

	onRowClick: PropTypes.func,

	onLastRowVisible: PropTypes.func,

	/** If we want to have selected rows, we can do this */
	rowSelection: ROW_SELECTION_PROP_TYPES.RowSelection,

	customRowClassFn: PropTypes.func,

	customCellClassFn: PropTypes.func,

	emptyComponent: PropTypes.node,
	loading: PropTypes.bool,
	lastRowLoading: PropTypes.bool,
	minWidthDisabled: PropTypes.bool,
	inputFocus: PropTypes.bool
};

SmartTable.defaultProps = {
	onClickSorting: () => {},
	currentSorting: null,
	computeRowIsDisabled: () => false,
	children: null,
	onRowClick: null,
	onLastRowVisible: null,
	rowSelection: null,
	customRowClassFn: () => undefined,
	customCellClassFn: () => undefined,
	emptyComponent: null,
	loading: false,
	lastRowLoading: false,
	data: [],
	minWidthDisabled: false,
	inputFocus: false
};

SmartTable.Paginator = Paginator;

export default SmartTable;
